import { Button, Typography } from '@design-system/index';
import { Card, Icon } from '@design-system/index';
import './SubscriptionsPage.scss';
import { useSubscriptionsPageVM } from './useSubscriptionsPageVM';
import { CotisationsLine, ExcelLine, ExcelFull, Upload, Checkmark } from '@icons';
import * as XLSX from 'xlsx';

export default function SubscriptionsPage() {
    const {
        importStatus,
        totalRows,
        importResult,
        errorMessage,
        fileInputRef,
        handleDrop,
        handleDragOver,
        handleInputChange,
        openFilePicker,
        reset,
    } = useSubscriptionsPageVM();

    const handleDownloadTemplate = () => {
        const data = [
            ['Matricule', 'Montant', 'Année', 'Commentaire'],
            ['00220', -17, 2025, 'PRELEV P*04/2025'],
            ['00220', -17, 2024, 'PREL.04/2024'],
        ];

        const ws = XLSX.utils.aoa_to_sheet(data);

        ws['!cols'] = [
            { wch: 12 },
            { wch: 10 },
            { wch: 8 },
            { wch: 25 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cotisations');

        XLSX.writeFile(wb, 'import-cotisations-template.xls', { bookType: 'xls' });
    };

    const isProcessing = importStatus === 'reading' || importStatus === 'uploading' || importStatus === 'importing';

    return (
        <Card
            className="subscriptions-card"
            headerType="full"
            headerIcon={<Icon icon={<CotisationsLine />} size={20} color="turquoise" />}
            headerTitle="Importation des cotisations"
            showBack={true}
        >
            <div className="template-download">
                <Button
                    variant="outlined"
                    color="primary"
                    icon={<Icon icon={<ExcelLine />} size={20} color="primary" />}
                    hoverIcon={<Icon icon={<ExcelFull />} size={20} color="primary" />}
                    onClick={handleDownloadTemplate}
                >
                    Télécharger le template d'import
                </Button>
            </div>

            <input
                type="file"
                ref={fileInputRef}
                accept=".xls,.xlsx"
                style={{ display: 'none' }}
                onChange={handleInputChange}
            />

            {importStatus === 'idle' && (
                <div
                    className="import-dropzone"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={openFilePicker}
                >
                    <Icon icon={<Upload />} size={24} color="primary" />
                    <Typography intrinsic="h3" variant="secondary" color="secondary" align="center">
                        Glisser/déposer un fichier
                        <br />
                        ou
                    </Typography>
                    <Button variant="primary" type="button" size="big" onClick={(e) => { e.stopPropagation(); openFilePicker(); }}>
                        Importer un fichier
                    </Button>
                </div>
            )}

            {isProcessing && (
                <div className="import-progress">
                    <div className="import-progress-header">
                        {importStatus === 'reading' && (
                            <Typography intrinsic="p" variant="p2" color="secondary">
                                Lecture du fichier...
                            </Typography>
                        )}
                        {importStatus === 'uploading' && (
                            <Typography intrinsic="p" variant="p2" color="secondary">
                                Upload du fichier en cours...
                            </Typography>
                        )}
                        {importStatus === 'importing' && (
                            <Typography intrinsic="p" variant="p2" color="secondary">
                                {totalRows > 0
                                    ? `Import de ${totalRows} cotisations en cours...`
                                    : 'Import des cotisations en cours...'}
                            </Typography>
                        )}
                    </div>
                    <div className="import-progress-bar">
                        <div className="import-progress-bar-track">
                            <div className="import-progress-bar-fill" />
                        </div>
                    </div>
                </div>
            )}

            {importStatus === 'complete' && importResult && (
                <div className="import-result">
                    <div className="import-result-icon">
                        <Icon icon={<Checkmark />} size={48} color="turquoise" />
                    </div>
                    <Typography intrinsic="h2" variant="secondary" color="secondary" align="center">
                        {importResult.created} cotisations créées
                    </Typography>
                    {importResult.updated > 0 && (
                        <Typography intrinsic="p" variant="p2" color="grey1" align="center">
                            {importResult.updated} cotisations mises à jour
                        </Typography>
                    )}
                    <Typography intrinsic="p" variant="p2" color="secondary" align="center">
                        {importResult.impacted_members} adhérents impactés
                    </Typography>
                    {importResult.not_found > 0 && (
                        <Typography intrinsic="p" variant="p2" color="error" align="center">
                            {importResult.not_found} adhérents non trouvés
                        </Typography>
                    )}
                    <Typography intrinsic="p" variant="p3" color="grey1" align="center">
                        {importResult.total} lignes traitées au total
                    </Typography>
                    <Button variant="outlined" color="primary" onClick={reset} style={{ marginTop: 16 }}>
                        Nouvel import
                    </Button>
                </div>
            )}

            {importStatus === 'error' && (
                <div className="import-error">
                    <Typography intrinsic="p" variant="p2" color="error" align="center">
                        {errorMessage ?? "Une erreur est survenue"}
                    </Typography>
                    <Button variant="outlined" color="primary" onClick={reset} style={{ marginTop: 16 }}>
                        Réessayer
                    </Button>
                </div>
            )}
        </Card>
    );
}
